import React, { useState } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { FormattedMessage } from 'react-intl';
import { Button, Toolbar, Typography } from '@mui/material';

import FileUploadDialog from '../trial/FileUploadDialog';
import ContentBox from '../layout/ContentBox';
import ApiClient from '../../loopback/apiClient';
import ZerpaTable, { ColumnObject } from '../layout/table/ZerpaTable';
import { Trial, VoucherManagementType } from '../../redux/types';
import { importVouchers } from '../utilities/ExcelImport';
import { fetchVoucherCode, showErrorNotification } from '../../redux/actions';
import { useAppDispatch } from '../../hooks/reduxHooks';

type Props = {
  trial?: Trial;
  voucherData?: VoucherManagementType[];
}

const columns: ColumnObject[] = [
  {
    keyName: 'code',
    text: <FormattedMessage id="voucher.couponCode" />,
    sortable: true,
  },
  {
    keyName: 'quipUserId',
    text: 'Q-Id',
    sortable: true,
  },
  {
    keyName: 'firstname',
    text: <FormattedMessage id="userDetails.firstname" />,
    sortable: true,
  },
  {
    keyName: 'surname',
    text: <FormattedMessage id="userDetails.surname" />,
    sortable: true,
  },
  {
    keyName: 'email',
    text: <FormattedMessage id="userDetails.email" />,
    sortable: true,
  },
  {
    keyName: 'claimedOn',
    text: <FormattedMessage id="voucher.claimedOn" />,
    sortable: true,
    isDate: true,
  },
  {
    keyName: 'enrollmentStatus',
    text: <FormattedMessage id="voucher.enrollmentStatus" />,
    sortable: true,
  },
];

const VoucherManagement: React.FC<Props> = ({ trial, voucherData }: Props) => {
  const dispatch = useAppDispatch();
  const [uploadDialogShow, setUploadDialogShow] = useState<boolean>(false);

  const closeUploadDialogHandler = () => setUploadDialogShow(false);
  const uploadVoucherHandler = () => setUploadDialogShow(true);

  const onUpload = async (input: HTMLInputElement | null) => {
    const file = input?.files && input.files[0];
    if (!file || !trial) {
      return;
    }
    let codeList: string[] = [];
    try {
      codeList = await importVouchers(file);
    } catch (e) {
      if ((e as Error).message === 'NO_DASH_FOUND') {
        dispatch(showErrorNotification(<FormattedMessage id="voucher.invalidFormatting" />));
      } else {
        dispatch(showErrorNotification(<FormattedMessage id="voucher.invalidCodeLength" />));
      }
      closeUploadDialogHandler();
      return;
    }
    try {
      await ApiClient.uploadVoucherData(trial.id, codeList);
    } catch (e) {
      dispatch(showErrorNotification((e as Error).message));
    }
    closeUploadDialogHandler();
    dispatch(fetchVoucherCode(trial.id));
  };

  return (
    <ContentBox title={<FormattedMessage id="voucher.voucherEnrollManagement" />}>
      <div>
        <Toolbar disableGutters>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>{trial?.name}</Typography>
          <Button onClick={uploadVoucherHandler} variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
            <FormattedMessage id="voucher.upload" />
          </Button>
        </Toolbar>
        <ZerpaTable<VoucherManagementType>
          keyIdentifier="id"
          tableRows={voucherData}
          columns={columns}
        />
      </div>
      <FileUploadDialog
        titleId="voucher.upload"
        onClose={closeUploadDialogHandler}
        isOpen={uploadDialogShow}
        upload={onUpload}
      />
    </ContentBox>
  );
};

export default VoucherManagement;
